import {
  Boxes,
  Check,
  ChevronRight,
  FolderKanban,
  MessageSquare,
  Plus,
  Search,
  Settings,
} from 'lucide-react'
import { BrandMark } from '@/components/BrandLogo'

/**
 * The hero image: one composition of overlapping surfaces.
 *
 * <p>A base window with the workspace question, a tall popup laid across its
 * lower-left corner, and a comparison card off the right edge. Depth is what
 * makes a product look like software you can reach into; the overlap lets one
 * image carry several ideas at once instead of needing a slideshow.
 *
 * <p>Three surfaces and nothing else. Loose chips floating over the artwork
 * read as stickers rather than parts of the product, so the panels carry it.
 *
 * <p>The panels are specific on purpose. The popup is the model switcher, and
 * the card is the one argument for running locally that a chart makes better
 * than a sentence.
 *
 * <p>The comparison card deliberately has no window chrome. It is an
 * illustration, not a screen NevTan has, and dressing it as one would imply a
 * reporting feature that does not exist. It carries no figures for the same
 * reason: the axes are unlabelled by value and the two verdicts are qualitative,
 * so the shape is the whole claim.
 *
 * <p>Below `lg` the layers unstack into ordinary flow, because overlapping
 * panels on a narrow screen are just clutter.
 */

const BASE = 'rounded-xl border border-brand-line bg-brand-paper shadow-raised'
const FLOATING =
  'rounded-xl border border-brand-line-strong bg-brand-paper shadow-float lg:ring-[7px] lg:ring-brand-page'

export function HeroComposition() {
  return (
    <div className="relative mx-auto max-w-5xl lg:h-[646px]">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-8 top-6 hidden h-2/3 rounded-[4rem] bg-brand/10 blur-3xl lg:block"
      />

      <BaseWindow />
      <ModelPopup />
      <ComparisonCard />

    </div>
  )
}

/* ------------------------------------------------------------ base window */

const RAIL = [
  { label: 'Chat', icon: <MessageSquare size={16} /> },
  { label: 'Models', icon: <Boxes size={16} /> },
  { label: 'Projects', icon: <FolderKanban size={16} /> },
  { label: 'Settings', icon: <Settings size={16} /> },
]

const RECENT = [
  { title: 'Refactor the payment service', meta: 'qwen-coder-7b · 12 messages' },
  { title: 'Summarise the Q3 board pack', meta: 'qwen3-8b · 4 messages' },
  { title: 'Draft the release notes', meta: 'llama-3.1-8b · 7 messages' },
  { title: 'Explain this stack trace', meta: 'qwen-coder-7b · 3 messages' },
  { title: 'Rewrite the onboarding email', meta: 'llama-3.1-8b · 9 messages' },
  { title: 'Compare the two proposals', meta: 'qwen3-8b · 6 messages' },
]

// Three, not four: a fourth chip slides under the comparison card and gets
// clipped mid-word.
const STARTERS = ['Explain some code', 'Summarise a document', 'Draft a reply']

function BaseWindow() {
  return (
    <div className={`${BASE} overflow-hidden lg:absolute lg:inset-x-6 lg:top-0`}>
      <div className="flex">
        {/* The app rail, kept narrow so the popup can sit against it. */}
        <aside className="hidden w-[58px] shrink-0 flex-col items-center gap-2 border-r border-brand-line bg-brand-panel py-3.5 sm:flex">
          {/* The rail is depicting NevTan itself, so it carries the real mark
              rather than a letter standing in for one. */}
          <BrandMark tone="teal" alt="" className="h-9 w-9" />
          <span className="mt-1 h-px w-6 bg-brand-line" />
          {RAIL.map((item, i) => (
            <span
              key={item.label}
              title={item.label}
              className={`flex h-9 w-9 items-center justify-center rounded-xl ${
                i === 0 ? 'bg-brand-tint text-brand-tint-ink' : 'text-brand-muted'
              }`}
            >
              {item.icon}
            </span>
          ))}
        </aside>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 border-b border-brand-line px-5 py-3">
            <span className="text-[11.5px] font-medium text-brand-muted">
              Apple M2 Pro · 16 GB · everything below runs here
            </span>
            <span className="ml-auto inline-flex shrink-0 items-center gap-1.5 rounded-full bg-brand-tint px-2.5 py-1 text-[10px] font-semibold text-brand-tint-ink">
              <span className="h-1.5 w-1.5 rounded-full bg-brand" /> Ready
            </span>
          </div>

          <div className="px-5 pb-8 pt-10 text-center sm:pt-14">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-brand-muted">
              NevTan workspace
            </p>
            <h3 className="mt-2.5 text-[28px] font-semibold leading-tight tracking-tight text-brand-heading sm:text-[38px]">
              What is your <span className="text-brand">job today?</span>
            </h3>

            <div className="mx-auto mt-6 flex max-w-md items-center gap-2.5 rounded-full border border-brand-line-strong bg-brand-panel px-4 py-3">
              <Search size={15} className="shrink-0 text-brand-muted" />
              <span className="text-[13px] text-brand-muted">
                Ask anything, or search your models
              </span>
            </div>

            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {STARTERS.map((starter) => (
                <span
                  key={starter}
                  className="rounded-full border border-brand-line bg-brand-paper px-3 py-1.5 text-[11.5px] text-brand-body"
                >
                  {starter}
                </span>
              ))}
            </div>
          </div>

          {/* Recent work, mostly covered by the popup — the reference does the
              same, and a half-seen list reads as depth rather than filler. */}
          <div className="border-t border-brand-line bg-brand-page px-5 pb-5 pt-5">
            <div className="grid gap-2.5 sm:grid-cols-3">
              {RECENT.map((item) => (
                <div
                  key={item.title}
                  className="rounded-xl border border-brand-line bg-brand-paper p-3.5"
                >
                  <p className="truncate text-[12px] font-semibold text-brand-heading">
                    {item.title}
                  </p>
                  <p className="mt-1 truncate text-[10.5px] text-brand-muted">{item.meta}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ---------------------------------------------------------- model popup */

const INSTALLED = [
  { id: 'qwen3-8b', use: 'General assistant', size: '4.7 GB', active: true },
  { id: 'qwen-coder-7b', use: 'Programming', size: '4.1 GB', active: false },
  { id: 'llama-3.1-8b', use: 'Writing', size: '4.9 GB', active: false },
]

const OPTIONS = ['Set as default', 'Check for updates', 'Remove from disk']

/**
 * The tall popup: narrow enough to read as a menu that has been torn off the
 * app, which is the shape the reference uses for its workspace switcher.
 */
function ModelPopup() {
  return (
    <div
      className={`${FLOATING} mt-4 overflow-hidden lg:absolute lg:bottom-0 lg:left-0 lg:z-10 xl:-left-4 lg:mt-0 lg:w-[252px]`}
    >
      <p className="px-3.5 pb-1 pt-3 text-[10.5px] font-medium text-brand-muted">
        Models on this computer
      </p>

      <div className="space-y-0.5 px-2 pb-1.5">
        {INSTALLED.map((model) => (
          <div
            key={model.id}
            className={`flex items-center gap-2.5 rounded-lg px-2 py-1.5 ${
              model.active ? 'bg-brand-tint/60 ring-1 ring-brand/25' : ''
            }`}
          >
            <span
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[11px] font-bold ${
                model.active ? 'bg-brand text-white' : 'bg-brand-panel text-brand-muted'
              }`}
            >
              {model.id.slice(0, 2).toUpperCase()}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[12.5px] font-semibold text-brand-heading">{model.id}</p>
              <p className="truncate text-[10px] text-brand-muted">{model.use}</p>
            </div>
            <span className="shrink-0 text-[9.5px] text-brand-muted">{model.size}</span>
          </div>
        ))}

        <div className="flex items-center gap-2.5 rounded-lg px-2 py-1.5">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-dashed border-brand-line-strong text-brand-muted">
            <Plus size={14} />
          </span>
          <p className="text-[12.5px] font-semibold text-brand-heading">Install a model</p>
        </div>
      </div>

      <div className="flex items-center justify-between border-y border-brand-line px-3.5 py-2.5">
        <span className="text-[12.5px] font-semibold text-brand-heading">This computer</span>
        <ChevronRight size={13} className="text-brand-muted" />
      </div>

      <p className="px-3.5 pb-0.5 pt-2.5 text-[10.5px] font-medium text-brand-muted">Model options</p>
      <ul className="pb-2">
        {OPTIONS.map((option) => (
          <li
            key={option}
            className="flex items-center justify-between px-3.5 py-1.5 text-[12.5px] text-brand-body"
          >
            {option}
            <ChevronRight size={13} className="text-brand-muted" />
          </li>
        ))}
      </ul>

      <p className="flex items-center gap-1.5 border-t border-brand-line bg-brand-panel px-3.5 py-2 text-[10px] text-brand-muted">
        <Check size={12} className="text-brand" /> 13.7 GB used · 188 GB free
      </p>
    </div>
  )
}

/* ------------------------------------------------------- comparison card */

const SERIES = {
  // Metered pricing climbs with every message sent.
  metered: 'M0,104 C40,96 70,80 110,64 C150,48 190,32 230,20 C260,12 280,8 300,5',
  // A model on your own machine costs the same at any volume.
  local: 'M0,112 L300,112',
}

function ComparisonCard() {
  return (
    <div
      className={`${FLOATING} mt-4 overflow-hidden lg:absolute lg:bottom-0 lg:right-0 lg:z-10 xl:-right-4 lg:mt-0 lg:w-[45%]`}
    >
      <div className="flex flex-wrap items-center gap-2 border-b border-brand-line bg-brand-panel px-4 py-3">
        <span className="text-[12.5px] font-semibold text-brand-heading">
          What heavy use costs
        </span>
      </div>

      <div className="px-4 pb-3 pt-3.5">
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10.5px]">
          <span className="inline-flex items-center gap-1.5 text-brand-muted">
            <span className="h-0.5 w-4 rounded-full bg-brand-line-strong" /> Metered AI service
          </span>
          <span className="inline-flex items-center gap-1.5 font-semibold text-brand">
            <span className="h-0.5 w-4 rounded-full bg-brand" /> NevTan, local model
          </span>
        </div>

        {/* Cost belongs on the vertical axis and volume on the horizontal one,
            so the label sits where the quantity it names actually runs. It is
            worded, not numbered: the chart shows the shape of the two curves,
            not a price anyone should hold us to. */}
        <div className="mt-2.5 flex gap-2">
          <span className="flex shrink-0 items-center justify-center rotate-180 text-[9.5px] font-semibold uppercase tracking-wider text-brand-muted [writing-mode:vertical-rl]">
            Monthly cost
          </span>

          <div className="min-w-0 flex-1">
            <svg
              viewBox="0 0 300 120"
              className="h-[132px] w-full"
              role="img"
              aria-label="Monthly cost of a metered AI service rises with the number of messages sent, while a model running locally stays flat."
            >
              {[16, 40, 64, 88].map((y) => (
                <line key={y} x1="0" y1={y} x2="300" y2={y} stroke="#e5e7eb" strokeWidth="1" />
              ))}

              <path d={`${SERIES.metered} L300,112 L0,112 Z`} fill="#dbe3ec" fillOpacity="0.55" />
              <path d={SERIES.metered} fill="none" stroke="#98a2b3" strokeWidth="2" />

              <path d={SERIES.local} fill="none" stroke="#0f766e" strokeWidth="2.5" />
              <circle cx="300" cy="112" r="3.5" fill="#0f766e" />
            </svg>

            <div className="flex justify-between text-[9.5px] text-brand-muted">
              <span>1k messages</span>
              <span>10k</span>
              <span>50k a month</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-px border-t border-brand-line bg-brand-line">
        <div className="bg-brand-paper px-4 py-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-brand-muted">
            Metered service
          </p>
          <p className="mt-0.5 text-[13.5px] font-semibold text-brand-heading">Rises with use</p>
        </div>
        <div className="bg-brand-paper px-4 py-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-brand">NevTan</p>
          <p className="mt-0.5 text-[13.5px] font-semibold text-brand-heading">
            Flat, whatever you send
          </p>
        </div>
      </div>

      <p className="border-t border-brand-line bg-brand-panel px-4 py-2.5 text-[10px] leading-relaxed text-brand-muted">
        Models running on your own hardware carry no per-message charge. Hosted models stay
        available through your own API key and are billed by that provider.
      </p>

    </div>
  )
}
